// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {
    getVsrMessage,
    hasVsrErrorForClaimField,
    isAfterStartDate,
    isResponsibleAccountEditable
} from '../../../../shared/services/claim-shared-helper';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import JdpClaim from '@deere/cps-it.warranty-model-factory/lib/claim-jd-promise';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../../shared/components/claim-fields/labeled-key-value-dropdown';
import LabeledTextAreaWithBlur from '../../../../shared/components/claim-fields/labeled-text-area-with-blur';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../../shared/components/claim-fields/responsible-accounts';
import {fixCursorPosition} from '../../../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../../../shared/services/format-pin';
import {isValidClaim} from '../../services/claim-validation-helper';
import {isValidDate} from '../../../../../shared/services/build-date';
import moment from 'moment';

function handleInputBlur(property, value) {

    this.actions.updateJdpClaimInputOnBlur(property, value, this.claim);

}

function handleDropDownBlur(property, event) {

    this.actions.updateJdpClaimInputOnBlur(property, event.target.value, this.claim);

}

function resetCustomerProductDetails() {

    this.actions.resetCkcCustomerFailure();
    this.actions.resetCustomerDetails();
    this.actions.resetJdpProductDetails();

}

function handlePinBlur(pin) {

    this::handleInputBlur('pin', pin);

    if (pin) {

        if (pin !== this.product.pin) {

            this::resetCustomerProductDetails();
            this.actions.readClaimProduct(pin);

        }

    } else {

        this::resetCustomerProductDetails();

    }

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'SAVE_VALD',
        id: 'SAVE_VALD',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}

function handleClaimPropertyChange(property, value) {

    this.actions.updateJdpClaimProperty(property, value);

}

function handlePinChange(pin, event) {

    const formattedPin = formatPin(pin);

    this.actions.updateJdpClaimProperty('pin', formattedPin);
    fixCursorPosition(event, formattedPin);

}

function handleWorkOrderChange(workOrder) {

    this.actions.updateJdpClaimWorkOrderOne(workOrder);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updateJdpClaimRepairNumber(repairNumber);

}

function handleReasonForReturnChange(event) {

    this.actions.updateReasonForReturnDropDown(event.target.value, this.reasonsForReturn);

}

function handleCustomerResolutionChange(event) {

    this.actions.updateCustomerResolutionDropDown(event.target.value, this.customerResolutions);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? date.target.value : null;

    this.actions.updateJdpClaimInputOnBlur(property, modifiedDate);

}

function handleGoodsInstallationVatChange(event) {

    this.actions.updateGoodsInstallationVat(event.target.value, this.goodsInstallationVatOptions);

}

const getReturnedDateErrorMessage = returnDate => returnDate === null ? 'RETURNED_DATE_IS_REQUIRED' : 'RETURNED_DATE_CANNOT_BE_BEFORE_DELIVERED_DATE';

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <h3>
                <FormattedMessage id='PRODUCT_INFORMATION'/>
            </h3>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledInputWithBlur
                    className='pin'
                    errorMessage='PIN_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                    isEditable={props.isEditable && (props.claim.claimStatus.key === 'ZDFT' || !props.claim.claimStatus.key)}
                    isValid={props.formValidation.pin}
                    label='PIN'
                    maxLength={18}
                    onBlur={props::handlePinBlur}
                    onChange={props::handlePinChange}
                    value={props.claim.pin}
                    vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                />
            </div>
            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={props::handleWorkOrderChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledDateInput
                    className='delivered-date'
                    date={props.claim.deliveryDate}
                    errorMessage='DELIVERED_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'deliveryDate', props.isEditable)}
                    isEditable={props.isEditable && props.product.isNonTracked}
                    isValid={props.formValidation.deliveryDate}
                    label='DELIVERED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'deliveryDate')}
                    onDateChange={handleClaimPropertyChange.bind(props, 'deliveryDate')}
                    vsrMessage={getVsrMessage(props.claim.errors, 'deliveryDate', props.isEditable)}
                />
                <LabeledDateInput
                    className='returned-date'
                    date={props.claim.returnDate}
                    errorMessage={getReturnedDateErrorMessage(props.claim.returnDate)}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'returnDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={isAfterStartDate(props.claim.returnDate, props.claim.deliveryDate) && props.formValidation.returnDate}
                    label='RETURNED'
                    maxDate={moment()}
                    minDate={moment.utc(props.claim.deliveryDate)}
                    onBlur={handleDateBlur.bind(props, 'returnDate')}
                    onDateChange={handleClaimPropertyChange.bind(props, 'returnDate')}
                    vsrMessage={getVsrMessage(props.claim.errors, 'returnDate', props.isEditable)}
                />
            </div>
            <div className='failure-info'>
                <LabeledInputWithBlur
                    className='failure-part'
                    errorMessage='FAILED_PART_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                    isEditable={props.isEditable && props.product.isNonTracked}
                    isValid={props.formValidation.failurePartNumber}
                    label='FAILED_PART'
                    onBlur={handleInputBlur.bind(props, 'failurePartNumber')}
                    onChange={handleClaimPropertyChange.bind(props, 'failurePartNumber')}
                    value={props.claim.failurePartNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                />
                {props.claim.showGoodsInstallationVat &&
                    <LabeledKeyValueDropDown
                        className='goods-installation-vat'
                        errorMessage='GOODS_INSTALLATION_VAT_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'goodsInstallationVat', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={true}
                        label='GOODS_INSTALLATION_VAT'
                        list={props.goodsInstallationVatOptions}
                        onChange={props::handleGoodsInstallationVatChange}
                        selectedValue={props.claim.goodsInstallationVat}
                        vsrMessage={getVsrMessage(props.claim.errors, 'goodsInstallationVat', props.isEditable)}
                    />
                }
            </div>
            <div className='jd-promise-fields'>
                <LabeledKeyValueDropDown
                    className='reason-for-return'
                    errorMessage='JDP_REASON_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'reasonForReturn', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.reasonForReturn}
                    label='JDP_REASON'
                    list={props.reasonsForReturn}
                    onBlur={handleDropDownBlur.bind(props, 'reasonForReturn')}
                    onChange={props::handleReasonForReturnChange}
                    selectedValue={props.claim.jdpReason}
                    vsrMessage={getVsrMessage(props.claim.errors, 'reasonForReturn', props.isEditable)}
                />
                <LabeledKeyValueDropDown
                    className='customer-resolution'
                    errorMessage='JDP_CUSTOMER_RESOLUTION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'customerResolution', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.customerResolution}
                    label='JDP_CUSTOMER_RESOLUTION'
                    list={props.customerResolutions}
                    onBlur={handleDropDownBlur.bind(props, 'customerResolution')}
                    onChange={props::handleCustomerResolutionChange}
                    selectedValue={props.claim.jdpCustomerResolution}
                    vsrMessage={getVsrMessage(props.claim.errors, 'customerResolution', props.isEditable)}
                />
                <LabeledTextAreaWithBlur
                    className='comments'
                    errorMessage='JDP_COMMENTS_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'jdpComments', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.comments}
                    label='JDP_COMMENTS'
                    onBlur={handleInputBlur.bind(props, 'comments')}
                    onChange={handleClaimPropertyChange.bind(props, 'jdpComments')}
                    value={props.claim.jdpComments}
                    vsrMessage={getVsrMessage(props.claim.errors, 'jdpComments', props.isEditable)}
                />
            </div>
            {(props.isEditable && !props.isValidClaim) &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim, props.product)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='SAVE_VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(JdpClaim).isRequired,
    customerResolutions: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    goodsInstallationVatOptions: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    isEditable: PropTypes.bool.isRequired,
    isValidClaim: PropTypes.bool.isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    reasonsForReturn: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/marketing/jdp/components/claim-details-section/claim-details-container.js